import { useState } from "react";
import { Dialog, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useTemplateActionFields } from "src/api/client-api/actionPlanTemplates/useTemplateActionOptions";
import type { TemplateAction } from "src/api/client-api/actionPlanTemplates/useActionPlanTemplate";
import { TemplateActionParameters } from "./TemplateActionParameters";
import { TemplateActionCatalogStep } from "./TemplateActionCatalogStep";

type Props = {
    ssqVersionId: number;
    actions: TemplateAction[];
    editing?: TemplateAction;
    onClose: () => void;
    onSave: (action: TemplateAction) => void;
};

export const TemplateActionDialog = (props: Props) => {
    const { ssqVersionId, actions, editing, onClose, onSave } = props;

    const { t } = useTranslation();
    const theme = useTheme();
    const [actionId, setActionId] = useState(editing?.actionId);
    const details = useTemplateActionFields(actionId, ssqVersionId);
    const fieldsError = details.isError;

    const saveLabel = t("settings.action_plan_templates.editor.save", "Save");
    const cancelLabel = t("settings.action_plan_templates.editor.cancel", "Cancel");

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            maxWidth="md"
            slotProps={{ paper: { sx: { maxWidth: theme.spacing(87.5) } } }}
            aria-labelledby="template-action-dialog-title"
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    pl: theme.spacing(3),
                    pr: theme.spacing(2),
                    py: theme.spacing(2),
                    borderBottom: 1,
                    borderColor: "divider",
                }}
            >
                <Typography variant="h5" id="template-action-dialog-title">
                    {t("settings.action_plan_templates.editor.add_action", "Add an action")}
                </Typography>
                <FoodsIconButton
                    icon="close"
                    size="m"
                    buttonProps={{
                        "aria-label": t("settings.action_plan_templates.editor.close", "Close"),
                        onClick: onClose,
                    }}
                />
            </Stack>
            {actionId !== undefined ?
                details.data ?
                    <TemplateActionParameters
                        key={`${actionId}-${ssqVersionId}-template-action-parameters`}
                        actionId={actionId}
                        title={details.data.title}
                        fields={details.data.actionFields}
                        editing={editing}
                        onClose={onClose}
                        onSave={onSave}
                    />
                :   <>
                        <DialogContent>
                            <Typography component="p" variant="body-regular" role={fieldsError ? "alert" : "status"}>
                                {fieldsError ?
                                    t(
                                        "settings.action_plan_templates.editor.fields_failed",
                                        "Unable to load action fields. Please try again.",
                                    )
                                :   t("settings.action_plan_templates.editor.loading_fields", "Loading action fields…")}
                            </Typography>
                            {fieldsError && (
                                <Button
                                    variant="text"
                                    onClick={() => {
                                        void details.refetch();
                                    }}
                                >
                                    {t("settings.action_plan_templates.retry", "Try again")}
                                </Button>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button variant="text" onClick={onClose} endIcon={<FoodsIcon icon="close" size={1} />}>
                                {cancelLabel}
                            </Button>
                            <Button variant="primary" disabled startIcon={<FoodsIcon icon="check" size={1} />}>
                                {saveLabel}
                            </Button>
                        </DialogActions>
                    </>

            :   <TemplateActionCatalogStep
                    ssqVersionId={ssqVersionId}
                    addedIds={actions.map((action) => action.actionId)}
                    onSelect={setActionId}
                    onClose={onClose}
                />
            }
        </Dialog>
    );
};
